/*
  Lifestyle gallery
  -----------------
  Clicking a lifestyle image shows it in the main gallery, and changing a
  variant afterwards takes the main image back to that variant.

  The theme does not get us all the way there on its own. product-info.js
  restores the main image in updateMedia, which opens with

    if (!variantFeaturedMediaId) return;

  so a variant with no image of its own is skipped entirely and whatever was
  clicked here stays on screen. That is not a rare case on these products: the
  250 media per product cap means some colourways have no mockup attached at
  all, and those are exactly the ones that would strand a customer on a
  lifestyle shot while they change colours and nothing appears to happen.

  So take the target from the variantChange payload, which carries
  featured_media.id directly, and only consult the rendered html when the
  variant has no image of its own. Reading html is not free: updateMedia moves
  nodes out of it rather than cloning them, so it is only safe on the branch
  where updateMedia returned early. See followRender for the detail.

  In that no-image branch, product-media-gallery.liquid marks the first
  RENDERED item active, which on a filtered wall art gallery is the first
  lifestyle shot. First rendered, not first in product.media: the filter drops
  the mockups ahead of it.

  Drives the theme's own <media-gallery> rather than duplicating it, so the
  slider, the zoom modal and the announcements all keep working.

  setActiveMedia is called with prepend: false because prepend reorders the
  gallery, moving the chosen item to the front of both the viewer and the
  thumbnail strip. Clicking through lifestyle shots would shuffle the gallery
  under the customer.

  Note for anyone tightening this later: prepend:true also dereferences the
  matching thumbnail with no null check (media-gallery.js), and it caches the
  strip in its constructor. That is harmless today only because the thumbnail
  strip is rendered from the unfiltered product.media, so every item has one.
  Filtering the strip to match the viewer would turn product-info.js's own
  prepend:true call into a TypeError mid-update.

  One delegated listener rather than per-button binding: a variant change
  replaces the gallery markup, and delegation survives that without needing to
  re-bind.
*/
(() => {
  if (window.__wwLifestyleGalleryBound) return;
  window.__wwLifestyleGalleryBound = true;

  const SELECTOR = '[data-ww-media-target]';

  // Highlight whichever button, if any, matches what the gallery is showing.
  // Scoped to one wrapper so a quick view or a second product section on the
  // page cannot clear the wrong one.
  const markChoice = (wrapper, mediaTarget) => {
    wrapper.querySelectorAll(SELECTOR).forEach((el) => {
      const chosen = el.dataset.wwMediaTarget === mediaTarget;
      el.classList.toggle('is-active', chosen);
      el.setAttribute('aria-current', chosen ? 'true' : 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(SELECTOR);
    if (!button) return;

    const wrapper = button.closest('.product__media-wrapper');
    const gallery = wrapper && wrapper.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    gallery.setActiveMedia(button.dataset.wwMediaTarget, false);
    markChoice(wrapper, button.dataset.wwMediaTarget);
  });

  // Runs after product-info.js has had its go at updateMedia, so this is a
  // correction rather than a race: it only moves the image when the theme
  // left it somewhere the fresh render disagrees with.
  const followRender = (event) => {
    const detail = event && event.data;
    if (!detail || !detail.html) return;

    // sectionId is the render's id, which is not always the one in the DOM:
    // product swap keeps the original in data-section. Media ids are built
    // from data-section, so match on one and build with the other.
    const info = [...document.querySelectorAll('product-info')].find(
      (el) => (el.dataset.originalSection || el.dataset.section) === detail.sectionId
    );
    if (!info) return;

    const wrapper = info.querySelector('.product__media-wrapper');
    const gallery = wrapper && wrapper.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    // Media ids are numeric, so the trailing segment is the media and
    // everything before it is the section that rendered it.
    let mediaId = detail.variant && detail.variant.featured_media && detail.variant.featured_media.id;

    // Only consult the render when the variant has no image of its own.
    //
    // detail.html is not a safe read once updateMedia has run: it MOVES the
    // new <li> nodes out of the parsed html and into the live gallery rather
    // than cloning them, so the variant's own item, which is exactly the one
    // that is new, has already been transplanted by the time this fires.
    // Searching html then finds no is-active at all, falls through to the
    // first item that is left, and that is a lifestyle shot. Which is the bug
    // this line used to be.
    //
    // updateMedia only runs when there is a featured media id, so in the
    // branch below html has not been touched and can be trusted.
    if (!mediaId) {
      const incoming =
        detail.html.querySelector('media-gallery li.is-active[data-media-id]') ||
        detail.html.querySelector('media-gallery li[data-media-id]');
      if (!incoming) return;
      mediaId = incoming.dataset.mediaId.split('-').pop();
    }

    const target = `${info.dataset.section}-${mediaId}`;

    // Only act when the two disagree. setActiveMedia scrolls the gallery back
    // into view if it has been scrolled past, which is unwelcome when the
    // image on screen is already the right one.
    const current = gallery.querySelector('li.is-active[data-media-id]');
    if (!current || current.dataset.mediaId !== target) {
      gallery.setActiveMedia(target, false);
    }

    // Clears the click highlight in the usual case, and keeps it when the
    // variant has no mockup and the gallery has fallen back to a lifestyle
    // shot, which is then genuinely what is on screen.
    markChoice(wrapper, target);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, followRender);
  }
})();
